// src/components/ui/DonutStat.tsx
// A generic multi-segment donut with a center value — Pre-Consult redesign
// Phase 4. Extracted from AilmentAssessmentEditor.tsx's due-checklist-only
// `DueChecklistRing` so any field/summary can use the same "N of M, split by
// tone" shape (today: Done/Due/Pending on the screening checklist).
//
// SVG stroke-dasharray arcs rather than a `background: conic-gradient(...)`
// div — this file lives under components/ui/**, where new inline
// `style={{}}` is banned by design-system lint, and a dynamic per-instance
// size/gradient genuinely cannot be expressed as static Tailwind classes.
// SVG presentation attributes (stroke, strokeDasharray, ...) are plain JSX
// props, not a style object, and every fill/stroke below is a theme.css
// token — same convention components/ui/illustrations/** already established.
import type { ReactNode } from 'react';

export type DonutTone = 'success' | 'warn' | 'danger' | 'neutral';

export interface DonutStatSegment {
  value: number;
  tone: DonutTone;
}

const TONE_STROKE: Record<DonutTone, string> = {
  success: 'var(--color-success-ink)',
  warn: 'var(--color-warn-ink)',
  danger: 'var(--color-danger-ink)',
  neutral: 'var(--color-control-line)',
};

const RADIUS = 42;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const STROKE_WIDTH = 16;

export interface DonutStatProps {
  segments: DonutStatSegment[];
  total: number;
  centerValue: ReactNode;
  centerLabel?: string;
  /** Outer diameter in px. */
  size?: number;
}

export function DonutStat({ segments, total, centerValue, centerLabel, size = 84 }: DonutStatProps) {
  if (total <= 0) return null;
  const nonZero = segments.filter(s => s.value > 0);

  // A single tone (everything current, or everything one status) renders as
  // one solid ring rather than a multi-arc gradient — same "all-current
  // renders solid" behaviour the original ring always had, and it sidesteps
  // a hairline seam a rounded-trip multi-arc can leave at the 360° join.
  let acc = 0;
  const arcs = nonZero.length > 1
    ? nonZero.map(seg => {
        const offset = (acc / total) * CIRCUMFERENCE;
        const length = (seg.value / total) * CIRCUMFERENCE;
        acc += seg.value;
        return { tone: seg.tone, offset, length };
      })
    : null;
  const soloTone: DonutTone = nonZero.length === 1 ? nonZero[0]!.tone : 'neutral';

  return (
    <div className="inline-flex flex-col items-center text-center">
      <div className="relative">
        <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true" focusable="false">
          <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="var(--color-muted)" strokeWidth={STROKE_WIDTH} />
          {arcs
            ? arcs.map((arc, i) => (
                <circle
                  key={i}
                  cx="50" cy="50" r={RADIUS} fill="none"
                  stroke={TONE_STROKE[arc.tone]}
                  strokeWidth={STROKE_WIDTH}
                  strokeDasharray={`${arc.length} ${CIRCUMFERENCE - arc.length}`}
                  strokeDashoffset={-arc.offset}
                  transform="rotate(-90 50 50)"
                />
              ))
            : (
              <circle cx="50" cy="50" r={RADIUS} fill="none" stroke={TONE_STROKE[soloTone]} strokeWidth={STROKE_WIDTH} />
            )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[19px] font-bold text-ink">
          {centerValue}
        </div>
      </div>
      {centerLabel && <div className="mt-1 text-[10.5px] text-ink-muted">{centerLabel}</div>}
    </div>
  );
}
